import React from "react";

/**
 * Compact inline error boundary for ONE section of a settings modal (or any
 * panel composed of independent sub-sections). When the wrapped subtree
 * throws during render, this draws a small inline fallback in place of the
 * section with a "Try again" button — the other sections keep rendering.
 *
 * Pass `label` so the fallback text can name the broken section
 * ("Billing", "Usage", etc.). The boundary logs the actual error to the
 * browser console so we can still triage post-mortem.
 *
 *   <SectionErrorBoundary label="Billing">
 *     <BillingPanel workspace={workspace} />
 *   </SectionErrorBoundary>
 */
export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, attempt: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `SectionErrorBoundary (${this.props.label || "section"}) caught:`,
      error,
      info
    );
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, attempt: s.attempt + 1 }));
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.label || "this section";
      const tid = this.props.testid || "section-error-boundary";
      return (
        <section
          className="space-y-2 mt-6"
          data-testid={tid}
          data-section-error="true"
        >
          <div className="rounded-xl border border-[#E5C8B4] bg-[#FDF4EC] px-4 py-3.5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#D96C4A]/15 text-[#7A4A2C] flex items-center justify-center shrink-0">
              <span aria-hidden="true" className="font-serif text-base">
                !
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-sans text-sm font-medium text-[#2C2A29]">
                {label} couldn&rsquo;t load
              </div>
              <p className="font-sans text-xs text-[#7A7571] mt-0.5 leading-relaxed">
                The rest of this page is fine — only this section hit an
                error. The other Settings sections still work.
              </p>
              <button
                type="button"
                onClick={this.reset}
                data-testid={`${tid}-retry`}
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#2C2A29] text-white font-sans text-[11px] font-medium hover:bg-[#1A1918] transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        </section>
      );
    }
    return (
      <React.Fragment key={this.state.attempt}>
        {this.props.children}
      </React.Fragment>
    );
  }
}
